import React, { Component } from "react";
import {
    MDBBtn,
    MDBIcon,
    MDBRow,
    MDBCol,
    MDBModalHeader,
    MDBModalBody,
    toast,
} from "mdbreact";
import OperatorIdDisplay from "./OperatorIdDisplay";
import "./MultiTenancy.css";

/**
 * Secondary Operator Permissions Settings — Mockup #4
 *
 * Lets a Primary Operator configure what Secondary Operators are allowed to do
 * across venues this operator owns.
 *
 * Props:
 *   operatorId, operatorName    - identity for the header
 *   initialPermissions          - the saved permission set
 *   onSave(permissions)         - called when the user clicks Save
 *   toggle                      - optional modal close handler if used inside a modal
 */
class SecondaryOperatorPermissions extends Component {
    constructor(props) {
        super(props);
        this.state = {
            permissions:
                props.initialPermissions || this.getDefaultPermissions(),
            isDirty: false,
            isSaving: false,
        };
    }

    getDefaultPermissions = () => ({
        // Venue Access
        viewVenue: true,
        editVenueSettings: false,
        viewEquipment: true,

        // Users & Subscriptions
        viewUsers: true,
        createUsers: false,
        editUsers: false,
        deleteUsers: false,
        viewSubscriptions: true,
        createSubscriptions: false,
        editSubscriptions: false,
        deleteSubscriptions: false,

        // Support & Tickets
        createResidentTickets: true,
        viewResidentTicketSummary: true,
        viewResidentTicketDetails: false,
        editResidentTickets: false,
        createPropertyTickets: true,
        viewPropertyTickets: true,
        editPropertyTickets: false,

        // Operator User Administration (intentionally locked off by default)
        addPropertyAdmin: false,
        removePropertyAdmin: false,
        addCustomerSupport: false,
        removeCustomerSupport: false,
    });

    togglePermission = (key) => {
        this.setState((prev) => ({
            permissions: { ...prev.permissions, [key]: !prev.permissions[key] },
            isDirty: true,
        }));
    };

    handleReset = () => {
        this.setState({
            permissions: this.getDefaultPermissions(),
            isDirty: true,
        });
    };

    handleSave = () => {
        const { onSave } = this.props;
        this.setState({ isSaving: true });
        if (onSave) {
            onSave(this.state.permissions);
        } else {
            toast.success("Secondary Operator permissions saved.", {
                closeButton: false,
            });
        }
        setTimeout(() => {
            this.setState({ isDirty: false, isSaving: false });
        }, 300);
    };

    renderPermissionCheckbox = (label, key, options = {}) => {
        const checked = !!this.state.permissions[key];
        return (
            <label
                key={key}
                className={`mt-perm-checkbox ${
                    options.locked ? "mt-perm-locked" : ""
                }`}
            >
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => this.togglePermission(key)}
                    disabled={options.locked}
                />
                <span>{label}</span>
                {options.note && (
                    <span className="mt-perm-note">{options.note}</span>
                )}
            </label>
        );
    };

    renderGroup = (title, icon, children) => {
        return (
            <div className="mt-perm-group">
                <h6 className="mt-perm-group-title">
                    <MDBIcon icon={icon} /> {title}
                </h6>
                <div className="mt-perm-group-body">{children}</div>
            </div>
        );
    };

    render() {
        const { isDirty, isSaving } = this.state;
        const { toggle, operatorId, operatorName } = this.props;

        return (
            <>
                {toggle && (
                    <MDBModalHeader toggle={toggle}>
                        <h4>Secondary Operator Permissions</h4>
                    </MDBModalHeader>
                )}
                <MDBModalBody>
                    <div className="mt-permissions-panel">
                        <p className="mt-panel-intro">
                            These settings apply to venues you own when another
                            operator is associated as Secondary Operator. They
                            do not affect your own users on venues you own.
                        </p>

                        <OperatorIdDisplay
                            operatorId={operatorId || "OP-3F9X-1024"}
                            operatorName={
                                operatorName ||
                                "RoamingiQ Demo Managed Properties"
                            }
                        />

                        {this.renderGroup(
                            "Venue Access",
                            "building",
                            <MDBRow>
                                <MDBCol md="6">
                                    {this.renderPermissionCheckbox(
                                        "View Venue",
                                        "viewVenue"
                                    )}
                                    {this.renderPermissionCheckbox(
                                        "View Equipment Status",
                                        "viewEquipment"
                                    )}
                                </MDBCol>
                                <MDBCol md="6">
                                    {this.renderPermissionCheckbox(
                                        "Edit Venue Settings",
                                        "editVenueSettings"
                                    )}
                                </MDBCol>
                            </MDBRow>
                        )}

                        {this.renderGroup(
                            "Users & Subscriptions",
                            "users",
                            <MDBRow>
                                <MDBCol md="6">
                                    <strong className="mt-perm-subhead">
                                        Users
                                    </strong>
                                    {this.renderPermissionCheckbox(
                                        "View Users",
                                        "viewUsers"
                                    )}
                                    {this.renderPermissionCheckbox(
                                        "Create Users",
                                        "createUsers"
                                    )}
                                    {this.renderPermissionCheckbox(
                                        "Edit Users",
                                        "editUsers"
                                    )}
                                    {this.renderPermissionCheckbox(
                                        "Delete Users",
                                        "deleteUsers"
                                    )}
                                </MDBCol>
                                <MDBCol md="6">
                                    <strong className="mt-perm-subhead">
                                        Subscriptions
                                    </strong>
                                    {this.renderPermissionCheckbox(
                                        "View Subscriptions",
                                        "viewSubscriptions"
                                    )}
                                    {this.renderPermissionCheckbox(
                                        "Create Subscriptions",
                                        "createSubscriptions"
                                    )}
                                    {this.renderPermissionCheckbox(
                                        "Edit Subscriptions",
                                        "editSubscriptions"
                                    )}
                                    {this.renderPermissionCheckbox(
                                        "Delete Subscriptions",
                                        "deleteSubscriptions"
                                    )}
                                </MDBCol>
                            </MDBRow>
                        )}

                        {this.renderGroup(
                            "Support & Tickets",
                            "life-ring",
                            <>
                                <strong className="mt-perm-subhead">
                                    Resident Tickets
                                </strong>
                                <MDBRow>
                                    <MDBCol md="6">
                                        {this.renderPermissionCheckbox(
                                            "Create Resident Tickets",
                                            "createResidentTickets"
                                        )}
                                        {this.renderPermissionCheckbox(
                                            "View Ticket Summary",
                                            "viewResidentTicketSummary"
                                        )}
                                    </MDBCol>
                                    <MDBCol md="6">
                                        {this.renderPermissionCheckbox(
                                            "View Ticket Details (PII)",
                                            "viewResidentTicketDetails",
                                            { note: "May expose subscriber PII" }
                                        )}
                                        {this.renderPermissionCheckbox(
                                            "Edit Resident Tickets",
                                            "editResidentTickets"
                                        )}
                                    </MDBCol>
                                </MDBRow>
                                <strong className="mt-perm-subhead mt-perm-subhead-spaced">
                                    Property Tickets
                                </strong>
                                <MDBRow>
                                    <MDBCol md="6">
                                        {this.renderPermissionCheckbox(
                                            "Create Property Tickets",
                                            "createPropertyTickets"
                                        )}
                                    </MDBCol>
                                    <MDBCol md="6">
                                        {this.renderPermissionCheckbox(
                                            "View Property Tickets",
                                            "viewPropertyTickets"
                                        )}
                                        {this.renderPermissionCheckbox(
                                            "Edit Property Tickets",
                                            "editPropertyTickets"
                                        )}
                                    </MDBCol>
                                </MDBRow>
                            </>
                        )}

                        {this.renderGroup(
                            "Operator User Administration",
                            "user-shield",
                            <>
                                <p className="mt-perm-warning">
                                    <MDBIcon icon="lock" /> These permissions
                                    are typically reserved for your own
                                    administrators.
                                </p>
                                <MDBRow>
                                    <MDBCol md="6">
                                        {this.renderPermissionCheckbox(
                                            "Add Property Admin",
                                            "addPropertyAdmin"
                                        )}
                                        {this.renderPermissionCheckbox(
                                            "Remove Property Admin",
                                            "removePropertyAdmin"
                                        )}
                                    </MDBCol>
                                    <MDBCol md="6">
                                        {this.renderPermissionCheckbox(
                                            "Add Customer Support",
                                            "addCustomerSupport"
                                        )}
                                        {this.renderPermissionCheckbox(
                                            "Remove Customer Support",
                                            "removeCustomerSupport"
                                        )}
                                    </MDBCol>
                                </MDBRow>
                            </>
                        )}

                        <div className="mt-info-note">
                            <MDBIcon icon="info-circle" /> Permission changes
                            apply immediately to all active Secondary Operator
                            associations.
                        </div>

                        <div className="mt-action-row mt-action-row-right">
                            <MDBBtn
                                color="secondary"
                                onClick={this.handleReset}
                                disabled={isSaving}
                            >
                                Reset to Defaults
                            </MDBBtn>
                            <MDBBtn
                                color="primary"
                                onClick={this.handleSave}
                                disabled={!isDirty || isSaving}
                            >
                                <MDBIcon icon="save" /> Save Permissions
                            </MDBBtn>
                        </div>
                    </div>
                </MDBModalBody>
            </>
        );
    }
}

export default SecondaryOperatorPermissions;
